import React from "react";
import DOMPurify from "dompurify";
import { ErrorType } from "../../../types/errorType";
import Icon from "components/Icon";
import styles from "./AlertMessage.module.scss";

interface MessageBoxProps {
  message: {
    type: "danger" | "error" | "warning" | "success" | "info";
    text: ErrorType | string | string[];
  };
  clearMessage?: () => void;
}

/*
  Component: AlertMessage
  Description: A React component that renders an alert message based on the provided message type and content.
  Props:
    - message: An object containing the type of the message and its content.
        - type: The type of the message, one of "danger", "error", "warning", "success", or "info".
        - text: The content of the message, which can be an ErrorType object, a string, or an array of strings.
    - clearMessage: A callback function to clear the error.
  Behavior:
    - Renders nothing if the message content is null or undefined.
    - If the message content is a string, renders a paragraph with the string content.
    - If the message content is an array of strings, renders paragraphs for each string in the array.
    - If the message content is an ErrorType object:
        - Renders a div containing the title, status code, and a list of error messages.
        - The title and status code are rendered as paragraphs if they exist.
        - Each error message is rendered as a list item (<li>) if its message property is not null.
        - The message content is sanitized using DOMPurify to prevent XSS attacks.
*/
const AlertMessage: React.FC<MessageBoxProps> = ({ message, clearMessage }) => {
  // Check if message.text is null or undefined
  if (!message.text) {
    return null; // Render nothing if text is null or undefined
  }

  const renderMessage = (text: ErrorType | string | string[]) => {
    if (typeof text === "string") {
      return <p>{text}</p>;
    } else if (Array.isArray(text)) {
      return text.map((item, index) => <p key={index}>{item}</p>);
    } else {
      return (
        <div>
          {clearMessage && <Icon name={"x"} onClick={clearMessage} className={styles.closeIcon} />}
          {text.title && <p>{text.title}</p>}
          {text.statusCode && <p>Status Code: {text.statusCode}</p>}
          <ul>
            {text.messages.map((msg, index) =>
              msg.message ? (
                <li key={index} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.message) }} />
              ) : null
            )}
          </ul>
        </div>
      );
    }
  };

  return (
    <div className={`alert alert-${message.type} ${styles.container}`} role="alert">
      {renderMessage(message.text)}
    </div>
  );
};

export default AlertMessage;
